/**
 * Kleinkram, den sich Kartei-Liste und Kartei-Blatt teilen.
 *
 * Die Texte stehen hier und nicht in `locales/`, so wie in `IcalCard` und
 * `ComplianceCards`: es sind rund achtzig kurze Zeichenketten, die nur an einer
 * Stelle vorkommen, und der gemeinsame Katalog wird davon nicht besser.
 */

import type { Database } from '../data/model'
import type { GapCode, GuestPolicy } from '../lib/guests'

type Bilingual = { de: string; en: string }

/** Die Schwellen und Fristen des Betriebs — aus `tenants`, siehe Migration 0043. */
export function policyOf(tenant: Database['tenant']): GuestPolicy {
  const t = tenant as Database['tenant'] & {
    vip_revenue_threshold?: number | null
    vip_stay_threshold?: number | null
    guest_retention_years?: number | null
    registration_retention_months?: number | null
  }
  return {
    vipRevenueThreshold: t.vip_revenue_threshold ?? null,
    vipStayThreshold: t.vip_stay_threshold ?? null,
    retentionYears: t.guest_retention_years ?? 7,
  }
}

/**
 * Was eine fehlende Angabe konkret KOSTET.
 *
 * Nicht „unvollständig", sondern die Folge: ein Vermieter, der liest „ohne E-Mail
 * keine Bestätigung möglich", weiß in derselben Sekunde, ob ihn das stört. Ein
 * Warndreieck ohne Grund erzieht nur dazu, Warndreiecke zu übersehen.
 */
export const GAP_TEXT: Record<GapCode, Bilingual> = {
  email: {
    de: 'Ohne E-Mail-Adresse: keine Bestätigung, kein Angebot, kein Check-in-Link.',
    en: 'Without an email address: no confirmation, no quote, no check-in link.',
  },
  phone: {
    de: 'Ohne Telefonnummer nicht erreichbar, wenn am Anreisetag etwas dazwischenkommt.',
    en: 'Unreachable on arrival day if anything goes wrong.',
  },
  address: {
    de: 'Anschrift fehlt — auf einer Rechnung über 400 € ist sie eine Pflichtangabe.',
    en: 'No address — required on an invoice above €400.',
  },
  country: {
    de: 'Herkunftsland fehlt; es gehört ins Gästeverzeichnis und in die Nächtigungsstatistik.',
    en: 'No country of origin; the guest register and the tourism statistics ask for it.',
  },
  birthdate: {
    de: 'Ohne Geburtsdatum lässt sich eine Ortstaxe-Befreiung nicht begründen.',
    en: 'Without a date of birth a tourist-tax exemption cannot be justified.',
  },
  language: {
    de: 'Ohne Sprache gehen Belege und Mails auf Deutsch hinaus.',
    en: 'Without a language, documents and mail go out in German.',
  },
}

export const SALUTATIONS: { value: string; de: string; en: string }[] = [
  { value: '', de: '—', en: '—' },
  { value: 'frau', de: 'Frau', en: 'Ms' },
  { value: 'herr', de: 'Herr', en: 'Mr' },
  { value: 'divers', de: 'Divers', en: 'Mx' },
  { value: 'familie', de: 'Familie', en: 'Family' },
  { value: 'firma', de: 'Firma', en: 'Company' },
]

export const DOC_TYPES: { value: string; de: string; en: string }[] = [
  { value: '', de: '—', en: '—' },
  { value: 'passport', de: 'Reisepass', en: 'Passport' },
  { value: 'id_card', de: 'Personalausweis', en: 'ID card' },
  { value: 'driving_licence', de: 'Führerschein', en: 'Driving licence' },
  { value: 'other', de: 'Anderes', en: 'Other' },
]

export const FLOORS: { value: string; de: string; en: string }[] = [
  { value: '', de: 'Keine Vorgabe', en: 'No preference' },
  { value: 'ground', de: 'Erdgeschoss', en: 'Ground floor' },
  { value: 'upper', de: 'Obergeschoss', en: 'Upper floor' },
  { value: 'any', de: 'Egal', en: 'Either' },
]

export const SMOKING: { value: string; de: string; en: string }[] = [
  { value: '', de: 'Keine Angabe', en: 'Not stated' },
  { value: 'non_smoker', de: 'Nichtraucher', en: 'Non-smoker' },
  { value: 'smoker', de: 'Raucher', en: 'Smoker' },
]

/**
 * Woher der Gast kam. Vorschläge, keine Vorschrift — das Feld nimmt jeden Text an,
 * weil ein Portalimport eines Tages etwas schreibt, das hier nicht steht.
 */
export const SOURCES: { value: string; de: string; en: string }[] = [
  { value: '', de: '—', en: '—' },
  { value: 'direct', de: 'Direkt', en: 'Direct' },
  { value: 'website', de: 'Eigene Website', en: 'Own website' },
  { value: 'recommendation', de: 'Empfehlung', en: 'Recommendation' },
  { value: 'returning', de: 'Stammgast', en: 'Returning guest' },
  { value: 'booking_com', de: 'Booking.com', en: 'Booking.com' },
  { value: 'airbnb', de: 'Airbnb', en: 'Airbnb' },
  { value: 'vrbo', de: 'Vrbo / FeWo-direkt', en: 'Vrbo' },
  { value: 'walk_in', de: 'Spontan vor Ort', en: 'Walk-in' },
  { value: 'other', de: 'Anderes', en: 'Other' },
]

export const HEALTH_BASIS: { value: string; de: string; en: string }[] = [
  { value: '', de: 'Bitte wählen …', en: 'Please choose …' },
  {
    value: 'consent',
    de: 'Der Gast hat ausdrücklich eingewilligt',
    en: 'The guest gave explicit consent',
  },
  {
    value: 'contract',
    de: 'Für die gebuchte Leistung erforderlich, vom Gast selbst mitgeteilt',
    en: 'Required for the booked service, stated by the guest',
  },
]

/**
 * Lädt ein Objekt als JSON-Datei herunter.
 *
 * Kein Umweg über den Server: die Auskunft ist eine Datei, die der Vermieter dem
 * Gast schickt, und sie soll den Browser nie verlassen haben, bevor er sie sieht.
 */
export function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json;charset=utf-8',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/** Dateiname ohne Umlaute, Leerzeichen und alles, was ein Mailprogramm ärgert. */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .normalize('NFD').replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'gast'
}
